import React, { useEffect, useRef, useState } from "react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { ArticleData } from "../../../network/api/ArticleData";
import Toast from "../../../../../components/Toast";
import ButtonLoader from "../../../../../components/ButtonLoader";
import Transition from "../../../../../components/Transition";
import FileManager from "../../../components/FileManager";
import FileUpload from "../../../components/FileUpload";
import { AnimatePresence } from "framer-motion";

export default function ArticleForm({ setEditMode, selectedArticle = null }) {
    const [content, setContent] = useState(selectedArticle?.content || "");
    const [title, setTitle] = useState(selectedArticle?.title || "");
    const [slug, setSlug] = useState(selectedArticle?.slug || "");
    const [category, setCategory] = useState(selectedArticle?.category || "legal");

    const [fileData, setFileData] = useState(null);

    const editorRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);

    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
    });

    useEffect(() => {
        setContent(selectedArticle?.content || "");
        setTitle(selectedArticle?.title || "");
        setCategory(selectedArticle?.category || "uncategorised");

        if (!selectedArticle) {
            setSlug("");
        } else {
            setSlug(selectedArticle.slug);
        }
    }, [selectedArticle]);

    useEffect(() => {
        if (fileData && editorRef.current) {
            const file = fileData;
            const ext = file.name.split('.').pop().toLowerCase();
            let insertHtml = "";

            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
                insertHtml = `<img src="${file.file_url}" alt="${file.name}" style="max-width:100%; height:auto; display:block; margin:1rem auto; border-radius:0.5rem;"/>`;
            } else if (ext === 'pdf') {
                insertHtml = `<a href="${file.file_url}" target="_blank">${file.name}</a>`;
            } else if (['doc', 'docx'].includes(ext)) {
                insertHtml = `<a href="${file.file_url}" target="_blank">${file.name}</a>`;
            } else if (['zip', 'rar', '7z'].includes(ext)) {
                insertHtml = `<a href="${file.file_url}" target="_blank">${file.name}</a>`;
            } else if (ext === 'mp4') {
                insertHtml = `<video controls style="max-width:100%;"><source src="${file.file_url}" type="video/mp4"></video>`;
            } else {
                insertHtml = `<a href="${file.file_url}" target="_blank">${file.name}</a>`;
            }

            const editor = editorRef.current;
            editor.model.change(writer => {
                const viewFragment = editor.data.processor.toView(insertHtml);
                const modelFragment = editor.data.toModel(viewFragment);
                editor.model.insertContent(modelFragment, editor.model.document.selection);
            });
        }
    }, [fileData]);

    const handleArticle = async () => {
        setIsLoading(true);
        // Validation: Title, Slug, and Category are required
        if (!title.trim() || !slug.trim() || !category.trim()) {
            setToastConfig({
                message: "Title, Slug, and Category are required.",
                type: "danger",
                visible: true,
            });
            setIsLoading(false);
            return;
        }

        try {
            const method = selectedArticle ? "PUT" : "POST";
            const id = selectedArticle?.id || null;

            const response = await ArticleData({ method, id, formData: { id, title, slug, content, category, } });

            if (response.status === "success") {
                setToastConfig({
                    message: selectedArticle ? "Article updated successfully!" : "Article created successfully!",
                    type: "success",
                    visible: true,
                });
                if (!selectedArticle) {
                    setTitle("");
                    setSlug("");
                    setContent("");
                    setCategory("legal");
                }
                //console.log(response);
                //setEditMode(false);
            } else {
                setToastConfig({
                    message: response.message || "Something went wrong.",
                    type: "danger",
                    visible: true,
                });
            }
        } catch (error) {
            setToastConfig({
                message: error.message || "An unexpected error occurred.",
                type: "danger",
                visible: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence mode="wait">
            <Transition key={"article-form-page"}>
                <Toast message={toastConfig.message}
                    type={toastConfig.type}
                    visible={toastConfig.visible}
                    setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                />

                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="card custom-card col-lg-8 col-md-8 col-12 ">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h4 className="card-title">{selectedArticle ? "Edit" : "Create"} Article</h4>
                                    <button className="btn btn-primary btn-sm" onClick={() => setEditMode(false)}>
                                        <i className="fa fa-arrow-left"></i> Back
                                    </button>
                                </div>
                                <div className="card-body custom-ekeditor">
                                    <div className="mb-3">
                                        <label className="form-label">Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={title}
                                            onChange={(e) => {
                                                const newTitle = e.target.value;
                                                setTitle(newTitle);
                                                if (!selectedArticle) {
                                                    setSlug(newTitle.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]+/g, ""));
                                                }
                                            }}
                                            placeholder="Enter article title"
                                            required={true}
                                        />
                                    </div>
                                    <div className="row ">
                                        <div className="mb-3 col-md-4 has-arrow col-lg-4 col-12">
                                            <label className="form-label">Category</label>
                                            <select
                                                className="form-control form-select select2-hidden-accessible"
                                                value={category}
                                                onChange={(e) => setCategory(e.target.value)}
                                                required={true}
                                            >
                                                <option value="uncategorised">Uncategorised</option>
                                                <option value="legal">Legal</option>
                                                <option value="help">Help</option>
                                            </select>

                                        </div>
                                        <div className="px-4 mb-3 col-lg-8 col-md-8 col-12">
                                            <label className="form-label">Slug</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={slug}
                                                onChange={(e) => setSlug(e.target.value)}
                                                placeholder="Enter article slug"
                                                required={true}
                                            />
                                        </div>

                                    </div>
                                    <div className="p-3 border rounded shadow-sm" >
                                        <CKEditor
                                            editor={ClassicEditor}
                                            data={content}
                                            onReady={(editor) => {
                                                editorRef.current = editor;
                                            }}
                                            onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setContent(data);
                                            }}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary mt-3"
                                        onClick={handleArticle}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? <ButtonLoader /> : <><i className="fa fa-save me-2"></i>Save Article</>}
                                    </button>
                                </div>
                            </div>
                            <div className=" col-lg-4 col-md-4 ">
                                <div className="row">
                                    <div className="col-12 ">
                                        <FileManager fileData={fileData} setFileData={setFileData} />
                                    </div>

                                </div>
                                <div className="row">
                                    <div className=" col-12 mb-3">
                                        <div className="card">
                                            <div className="card-body">
                                                <h4 className="card-title">Upload Files</h4>
                                                <p className="text-muted">Upload files to the article. You can upload images, videos, and documents.</p>
                                                <FileUpload mode="files" setFileData={setFileData} description={"Article File or Image"} />
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </AnimatePresence>
    );
}